import { useEffect, useState } from "react";
import { Download, FileWarning, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const kindOf = (ct = "") => {
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  if (ct === "application/pdf") return "pdf";
  if (ct.startsWith("text/")) return "text";
  return "other";
};

export const downloadDocument = async (url, filename) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "document";
    a.click();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  } catch (e) {
    toast.error(errMsg(e, "Document indisponible"));
  }
};

const usePreviewBlob = (doc, url, kind) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    if (!doc || kind === "other") return undefined;
    let created;
    api
      .get(`${url}${url.includes("?") ? "&" : "?"}action=preview`, { responseType: "blob" })
      .then(({ data }) => {
        created = URL.createObjectURL(new Blob([data], { type: doc.content_type || undefined }));
        setBlobUrl(created);
      })
      .catch((e) => setErreur(errMsg(e, "Aperçu indisponible")));
    return () => created && URL.revokeObjectURL(created);
  }, [doc, url, kind]);

  return { blobUrl, erreur };
};

const ViewerContent = ({ kind, blobUrl, doc, zoom }) => {
  if (kind === "pdf" || kind === "text") {
    return (
      <iframe
        title={`Aperçu ${doc.titre || doc.filename}`}
        src={blobUrl}
        data-testid={kind === "pdf" ? "viewer-pdf" : "viewer-text"}
        className="w-full h-full min-h-[70vh] rounded-lg bg-white"
      />
    );
  }
  if (kind === "image") {
    return (
      <img
        src={blobUrl}
        alt={doc.titre}
        data-testid="viewer-image"
        style={{ transform: `scale(${zoom})` }}
        className="max-w-full max-h-full origin-center transition-transform duration-200"
      />
    );
  }
  return <video src={blobUrl} controls data-testid="viewer-video" className="max-w-full max-h-full rounded-lg" />;
};

export default function DocumentViewer({ doc, url, onClose }) {
  const kind = kindOf(doc?.content_type);
  const [zoom, setZoom] = useState(1);
  const { blobUrl, erreur } = usePreviewBlob(doc, url, kind);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  if (!doc) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col" data-testid="document-viewer">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 text-white shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate" data-testid="viewer-title">{doc.titre || doc.filename}</div>
          <div className="text-[11px] text-white/50 truncate">{doc.filename} · {Math.round((doc.size || 0) / 1024)} Ko</div>
        </div>
        {kind === "image" && (
          <div className="hidden sm:flex items-center gap-1 text-xs">
            <button data-testid="viewer-zoom-out" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20">−</button>
            <span className="w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button data-testid="viewer-zoom-in" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20">+</button>
          </div>
        )}
        <button
          data-testid="viewer-download-btn"
          onClick={() => downloadDocument(url, doc.filename)}
          className="flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[#005CFF] hover:bg-[#0047CC] transition-colors"
        >
          <Download size={14} /> Télécharger
        </button>
        <button data-testid="viewer-close-btn" onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-auto grid place-items-center p-4">
        {kind === "other" && (
          <div className="text-center text-white/70 max-w-sm space-y-3" data-testid="viewer-no-preview">
            <FileWarning size={30} className="mx-auto text-white/40" />
            <p className="text-sm">Ce format ne peut pas être prévisualisé dans l'application. Téléchargez le fichier pour l'ouvrir.</p>
          </div>
        )}
        {erreur && <div className="text-sm text-red-300" data-testid="viewer-error">{erreur}</div>}
        {kind !== "other" && !blobUrl && !erreur && <Loader2 size={26} className="animate-spin text-white/60" />}
        {blobUrl && <ViewerContent kind={kind} blobUrl={blobUrl} doc={doc} zoom={zoom} />}
      </div>
    </div>
  );
}
